/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  createContext,
  useState,
  useContext,
  useCallback,
  useEffect,
  useMemo,
} from 'react'
import { useRouteMatch } from 'react-router-dom'
import Firebase from '@/modules/Firebase/classes/Firebase'
import { IFirebaseAuthentication } from '@/modules/Firebase/firebase-types'
import { useRealtimeAttendanceContext } from '@/modules/RealtimeAttendance/contexts/RealtimeAttendanceContext'
import { useAuthContext } from '@/modules/Auth/contexts/AuthContext'
import {
  IMMEDIATE_NODE,
  SCHEDULED_NODE,
  NOSHOW_NODE,
  ATTACHMENTS_NODE,
  STRATIFICATION_NODE,
} from '@/modules/Firebase/firebase-constants'
import usePageVisible from '@/components/Hooks/usePageVisible'
import { getCache } from '@/utils/cache'
import { LOGGED_IN_WITH_MAGIC_LINK } from '@/modules/Auth/pages/MagicLink/consts'
import { EventParams, useAnalyticsContext } from '@conexasaude/analytics'
import { FunctionComponentChildren } from '@/@types/react'
import { getDecJson } from '@/utils/crypt'
import { getFirebaseToken } from '../services'

export interface IFirebaseContext {
  firebase: Firebase
  initializeFirebase(
    connectData: IFirebaseAuthentication,
    firebaseUserToken: string,
    clinicId: number,
    patientId: number,
  ): void
  logoutFromFirebase(): void
  logAnalyticsEvent: (eventName: string, eventParams?: EventParams) => void
}

const FirebaseContext = createContext<IFirebaseContext>(null)

export const FirebaseProvider: FunctionComponentChildren = ({ children }) => {
  const isHomeRoute = useRouteMatch({
    path: '/',
    exact: true,
  })
  const isMeetRoute = useRouteMatch({
    path: '/chamada',
  })
  const [firebase, setFirebase] = useState<Firebase>()
  const [firebaseToken, setFirebaseToken] = useState<string>()
  const [isEnable, setIsEnable] = useState<boolean>(false)
  const { updateRealtimeAttendance } = useRealtimeAttendanceContext()
  const { user, updateUser, clinic } = useAuthContext()
  const { sendAnalyticsEvent } = useAnalyticsContext()

  usePageVisible(visible => {
    firebase?.patientOnline(visible)
  })

  const initializeFirebase = useCallback(
    (
      connectData: IFirebaseAuthentication,
      firebaseUserToken: string,
      clinicId: number,
      patientId: number,
    ) => {
      const firebase = new Firebase(
        connectData,
        firebaseUserToken,
        clinicId,
        patientId,
        token => {
          updateUser(user => {
            user.firebaseToken = token
            return user
          })
        },
      )
      setFirebase(firebase)
      firebase.onAdd(data => {
        updateRealtimeAttendance(realtimeAttendance => {
          realtimeAttendance.setImmediateByFirebase(data)
          return realtimeAttendance
        })
      }, IMMEDIATE_NODE)
      firebase.onChange(data => {
        updateRealtimeAttendance(realtimeAttendance => {
          realtimeAttendance.setImmediateByFirebase(data)
          return realtimeAttendance
        })
      }, IMMEDIATE_NODE)
      firebase.onRemove(data => {
        updateRealtimeAttendance(realtimeAttendance => {
          realtimeAttendance.removeByFirebase(data, IMMEDIATE_NODE)
          return realtimeAttendance
        })
      }, IMMEDIATE_NODE)
      firebase.onAdd(data => {
        updateRealtimeAttendance(realtimeAttendance => {
          realtimeAttendance.setScheduledByFirebase(data)
          return realtimeAttendance
        })
      }, SCHEDULED_NODE)
      firebase.onChange(data => {
        updateRealtimeAttendance(realtimeAttendance => {
          realtimeAttendance.setScheduledByFirebase(data)
          return realtimeAttendance
        })
      }, SCHEDULED_NODE)
      firebase.onRemove(data => {
        updateRealtimeAttendance(realtimeAttendance => {
          realtimeAttendance.removeByFirebase(data, SCHEDULED_NODE)
          return realtimeAttendance
        })
      }, SCHEDULED_NODE)
      firebase.onAdd(data => {
        updateRealtimeAttendance(realtimeAttendance => {
          realtimeAttendance.setAttachmentsByFirebase(data)
          return realtimeAttendance
        })
      }, ATTACHMENTS_NODE)
      firebase.onChange(data => {
        updateRealtimeAttendance(realtimeAttendance => {
          realtimeAttendance.setAttachmentsByFirebase(data)
          return realtimeAttendance
        })
      }, ATTACHMENTS_NODE)
      firebase.onRemove(data => {
        updateRealtimeAttendance(realtimeAttendance => {
          realtimeAttendance.setAttachmentsByFirebase(data)
          return realtimeAttendance
        })
      }, ATTACHMENTS_NODE)
      firebase.onAdd(data => {
        updateRealtimeAttendance(realtimeAttendance => {
          realtimeAttendance.setNoShowByFirebase(data)
          return realtimeAttendance
        })
      }, NOSHOW_NODE)
      firebase.onChange(data => {
        updateRealtimeAttendance(realtimeAttendance => {
          realtimeAttendance.setNoShowByFirebase(data)
          return realtimeAttendance
        })
      }, NOSHOW_NODE)
      firebase.onRemove(data => {
        updateRealtimeAttendance(realtimeAttendance => {
          realtimeAttendance.removeByFirebase(data, NOSHOW_NODE)
          return realtimeAttendance
        })
      }, NOSHOW_NODE)
      firebase.onAdd(data => {
        updateRealtimeAttendance(realtimeAttendance => {
          realtimeAttendance.setStratificationByFirebase(data)
          return realtimeAttendance
        })
      }, STRATIFICATION_NODE)
      firebase.onChange(data => {
        updateRealtimeAttendance(realtimeAttendance => {
          realtimeAttendance.setStratificationByFirebase(data)
          return realtimeAttendance
        })
      }, STRATIFICATION_NODE)
      firebase.onRemove(data => {
        updateRealtimeAttendance(realtimeAttendance => {
          realtimeAttendance.removeByFirebase(data, STRATIFICATION_NODE)
          return realtimeAttendance
        })
      }, STRATIFICATION_NODE)
      firebase?.patientOnline(true)
    },
    [updateRealtimeAttendance, updateUser],
  )

  const logAnalyticsEvent = useCallback(
    (eventName: string, eventParams?: EventParams) => {
      sendAnalyticsEvent(eventName, {
        ...eventParams,
        options: {
          sendTo: ['DEFAULT_GTAG_INSTANCE', 'DEFAULT_CLARITY_INSTANCE'],
        },
      })
    },
    [sendAnalyticsEvent],
  )

  const logoutFromFirebase = useCallback(() => {
    setIsEnable(false)
    if (firebase) {
      firebase?.patientOnline(false)
      setFirebase(firebase => firebase?.destroy())
    }
  }, [firebase])

  useEffect(() => {
    if (user) {
      getFirebaseToken().then(token => {
        setFirebaseToken(token)
      })
    }
  }, [user])

  useEffect(() => {
    if (!user || !clinic || isEnable || !firebaseToken) return

    const has2FA = clinic?.flag?.HabilitarAutenticacaoDoisFatoresLoginPaciente
    const isFromMagicLink = getCache(LOGGED_IN_WITH_MAGIC_LINK)

    if (
      !has2FA ||
      (has2FA && isFromMagicLink) ||
      (has2FA && !isFromMagicLink && (isHomeRoute || isMeetRoute))
    ) {
      const fbConfig = getDecJson(import.meta.env.VITE_FB_CFG)

      initializeFirebase(
        fbConfig,
        firebaseToken,
        user.clinic.id,
        user.patientId,
      )
      setIsEnable(true)
    }
  }, [
    user,
    isEnable,
    initializeFirebase,
    isHomeRoute,
    isMeetRoute,
    clinic,
    firebaseToken,
  ])

  const firebaseValue = useMemo(() => {
    return {
      firebase,
      initializeFirebase,
      logoutFromFirebase,
      logAnalyticsEvent,
    }
  }, [firebase, initializeFirebase, logoutFromFirebase, logAnalyticsEvent])

  return (
    <FirebaseContext.Provider value={firebaseValue}>
      {children}
    </FirebaseContext.Provider>
  )
}

export const useFirebaseContext = (): IFirebaseContext => {
  const context = useContext(FirebaseContext)
  if (!context) {
    throw new Error('useAuthContext must be used within a FirebaseProvider')
  }
  return context
}
